import {ISessionInfo} from '../../ts/ServerApi/types/ISessionInfo';
import {AuthenticationType} from '../../settings/types/IServerSettingsUnauthenticated';
import WebHttpClient from '../../httpClient/WebHttpClient';
import {GeneratePublicKeyRequest, IGeneratePublicKeyResult} from '../../ts/ServerApi/requests/GeneratePublicKeyRequest';
import {ILoginParams} from '../../ts/ServerApi/types/ILoginParams';
import _ from 'underscore';
import {LoginRequest} from '../../ts/ServerApi/requests/LoginRequest';
import {IGetSiteSamlLoginInfoResult} from '../../ts/ServerApi/types/IGetSiteSamlLoginInfoResult';
import {GetSiteSamlLoginInfoRequest} from '../../ts/ServerApi/requests/GetSiteSamlLoginInfoRequest';
import {CommonErrorCode, LoginErrorCodes, UserErrorCode} from '../../ts/helpers/ErrorCodes';
import {ISiteLoginParams} from '../../ts/ServerApi/types/ISiteLoginParams';
import {Request} from '../../httpClient/serverApi/requests/Request';
import {IGetSessionInfoResult} from '../../ts/ServerApi/requests/GetSessionInfoRequest';
import {SspiLoginRequest} from '../../ts/ServerApi/requests/SspiLoginRequest';
import {KerberosLoginRequest} from '../../ts/ServerApi/requests/kerberosLoginRequest';
import {ClientCertificateLoginRequest} from '../../ts/ServerApi/requests/ClientCertificateLoginRequest';
import {ILogoutResult, LogoutRequest} from '../../ts/ServerApi/requests/LogoutRequest';
import {ServerSettingsUnauthenticated} from '../../settings/services/ServerSettingsUnauthenticated';
import {WindowLocationService} from '../../commonInfra/services/WindowLocationService';
import {LoginUrlsWrapper} from '../../app/spaces/Login/LoginUrls';
import {getAppStore} from '../../app/AppStore';
import {
  ErrorReason,
  getSessionManagementServiceInstance, SiteDefaultAuth,
} from '../../sessionManagement/SessionManagementService';
import {ServiceLocator} from '../../app/ServiceLocator';
import {IHttpService, IQService} from 'angular';
import {Messages} from '../../codegen/Localize';

class AuthenticationService {

  protected authenticationTypeOverride: AuthenticationType;

  constructor() {
  }

  public login(username: string, password: string, siteUrlName?: string): ng.IPromise<void> {
    return getSessionManagementServiceInstance().sessionInfoRequest(() => {
      return WebHttpClient.requestPost(new GeneratePublicKeyRequest())
        .then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
          const loginParams: ILoginParams = {
            username: username,
            encryptedPassword: rsa.encrypt(password, generatePublicKeyResult.key),
            keyId: generatePublicKeyResult.keyId,
          };
          if (_.isString(siteUrlName)) {
            loginParams.siteUrlName = siteUrlName;

          }
          const loginRequest = new LoginRequest(loginParams);
          return WebHttpClient.requestPost(loginRequest)
            .then((loginResult: ISessionInfo) => {
              if (loginResult.errors) {
                return this.getQService().reject(loginResult.errors[0]);
              }
              return getSessionManagementServiceInstance().applySessionInfoInClient(loginResult);
            });
        });
    });
  }

  public siteSamlLogin(username: string): Promise<IGetSiteSamlLoginInfoResult> {
    const loginParams: ILoginParams = {
      username: username,
      encryptedPassword: null,
      keyId: null,
    };
    return WebHttpClient.requestPost(new GetSiteSamlLoginInfoRequest(loginParams))
      .then<IGetSiteSamlLoginInfoResult>((getSiteSamlLoginInfoResult: IGetSiteSamlLoginInfoResult) => {
        if (!getSiteSamlLoginInfoResult) {
          return Promise.reject(UserErrorCode.UserNotSupportedGuestOperation);
        } else if (getSiteSamlLoginInfoResult.errors) {
          return Promise.reject(getSiteSamlLoginInfoResult.errors[0]);
        }
        // handle success
        return Promise.resolve(getSiteSamlLoginInfoResult);
      }, (error) => { // handle when http status code is not 2xx
        if (error) {
          return Promise.reject(error);
        }
        return Promise.reject(UserErrorCode.UserNotSupportedGuestOperation);
      });
  }

  public autoLogin(siteUrlName?: string): ng.IPromise<void> {
    return getSessionManagementServiceInstance().sessionInfoRequest(() => {
      const loginParams: ISiteLoginParams = {};
      if (_.isString(siteUrlName)) {
        loginParams.siteUrlName = siteUrlName;
      }
      const authType = this.authenticationType();
      let loginRequest: Request<ISiteLoginParams, IGetSessionInfoResult>;
      switch (authType) {
        case AuthenticationType.Sspi:
          loginRequest = new SspiLoginRequest(loginParams);
          break;
        case AuthenticationType.Kerberos:
          loginRequest = new KerberosLoginRequest(loginParams);
          break;
        case AuthenticationType.ClientCertificate:
        case AuthenticationType.ClientCertificateWithFallback:
          loginRequest = new ClientCertificateLoginRequest(loginParams);
          break;
      }
      // This must be GET to get around an IE "feature". See B144390
      return WebHttpClient.requestGet(loginRequest)
        .then((sessionInfo: ISessionInfo) => {
          if (sessionInfo.errors) {
            return this.getQService().reject(sessionInfo.errors[0] && sessionInfo.errors[0].code);
          }
          return getSessionManagementServiceInstance().applySessionInfoInClient(sessionInfo);
        }, (reason: number) => {
          getSessionManagementServiceInstance().clearSessionInfo();
          if (reason === ErrorReason.Unauthorized || reason === ErrorReason.Unknown) {
            switch (authType) {
              case AuthenticationType.Sspi:
                reason = LoginErrorCodes.SspiFailed;
                break;
              case AuthenticationType.Kerberos:
                reason = LoginErrorCodes.KerberosFailed;
                break;
              case AuthenticationType.ClientCertificate:
              case AuthenticationType.ClientCertificateWithFallback:
                reason = LoginErrorCodes.ClientCertificateNotFound;
                break;
            }
          }
          return this.getQService().reject(reason);
        });
    });
  }

  public signOut() {
    WebHttpClient.requestPost(new LogoutRequest())
      .then((logoutResult: ILogoutResult) => {
        if (ServerSettingsUnauthenticated.getAuthenticationType() === AuthenticationType.Saml &&
          this.isSAMLLogoutEndpoint(logoutResult.redirectUrl)) {
          // For Server-Wide SAML, we will do a special bit of logic.
          this.performSAMLLogout(logoutResult);
        } else {
          // This causes all scripts to reload, so all client side app state will be cleared.
          WindowLocationService.navigateTo(logoutResult.redirectUrl || '/');
        }
      });
  }

  /**
   * We have a special endpoint for the server-wide SAML SingleLogout scenario that performs the logout logic
   * instead of the standard logout request.
   */
  private isSAMLLogoutEndpoint(targetRelativeURL: string) {
    // The Spring Filter is registered against this URL.
    return targetRelativeURL.includes('/saml/logout');
  }

  /**
   * To do a SAML Logout, the initial logout returns us the SAML Single-Logout URL.
   * That endpoint will perform logout and return an HTML page that onload makes a POST request to
   * the configured IdP to cascade the logout. (This is a standard SAML POST-Binding).
   * The IdP will eventually redirect the user back to our configured logout landing page.
   */
  private performSAMLLogout(logoutResult: ILogoutResult) {
    // Note: We can't use a browser redirect or browser form action to perform this because we need to
    //       include the XSRF header in the request! So the alternative is to send the post through Ajax
    //       and load the response html onto the page. TFSID: 735765
    this.getNgHttpService()({
      method: 'POST',
      url: logoutResult.redirectUrl,
      data: {},
    }).then((successResponse: any) => {
      if (successResponse && successResponse.data) {
        document.open();
        document.write(successResponse.data);
        document.close();
      }
    });
  }


  public authenticationType(): AuthenticationType {
    return this.authenticationTypeOverride ?? ServerSettingsUnauthenticated.getAuthenticationType();
  }

  // Anonymous is an angular state concept that means the user doesn't
  // have to be authenticated to view this url.
  public isAnonymousUrl(url: string): boolean {
    const loginUrls = LoginUrlsWrapper.getInstance();
    return url.includes('/#/authenticate')
      || url.includes(loginUrls.signinPath())
      || url.includes(loginUrls.signinErorrCodePath(''))
      || url.includes(loginUrls.signedOutPath())
      || url.includes(loginUrls.siteSamlSignedOutPath())
      || url.includes(loginUrls.passwordResetUserNameBasePath())
      || url.includes(loginUrls.requestPasswordResetPath())
      || url.includes('/#/pkce/');
  }

  public handle401(reason: number) {
    // If 1...we are not in a state (i.e. app has not bootstrapped)
    // or 2...we are in an anonymous state
    // or 3...the api responds 'Permission Denied' (NOT session expired)
    if (getAppStore().getState().rootAdminUiState !== 'default' ||
      this.isAnonymousUrl(WindowLocationService.getHref()) ||
      reason === CommonErrorCode.CallingUserInsufficientPermissions) {
      return;
    }
    if (this.isAuthenticationAutoLogin() || this.isAuthenticationExternal()) {
      // For these auth types we can't give an error message that the user's session expired
      // so just refresh the page and let vizportal re-bootstrap and re-authenticate
      WindowLocationService.reload();
    } else {
      // For other auth types we can give a "session expired" error message, so do that
      const redirectPath = WindowLocationService.getPathQueryFragment();
      const href = LoginUrlsWrapper.getInstance().signinUrl({
        redirect: redirectPath ? redirectPath.replace(/^\/#/, '') : null,
        error: reason || CommonErrorCode.InvalidSession,
      });
      // We must remove the hash so the browser does a full page load. The /signin route is a special
      // route that apache will rewrite to /#/signin with query params intact
      WindowLocationService.navigateTo(href.replace(/^#/, ''));
    }
  }

  public overrideAuthenticationType(authType: AuthenticationType) {
    this.authenticationTypeOverride = authType;
  }

  public isAuthenticationAutoLogin(): boolean {
    return this.isWindowsCredentialsAutoLogin() ||
      this.authenticationType() === AuthenticationType.ClientCertificate ||
      this.authenticationType() === AuthenticationType.ClientCertificateWithFallback;
  }

  public isWindowsCredentialsAutoLogin(): boolean {
    return this.authenticationType() === AuthenticationType.Sspi ||
      this.authenticationType() === AuthenticationType.Kerberos;
  }

  public isAuthenticationSaml(): boolean {
    return this.authenticationType() === AuthenticationType.Saml;
  }

  public isAuthenticationOpenId(): boolean {
    return this.authenticationType() === AuthenticationType.OpenId;
  }

  public isAuthenticationExternal(): boolean {
    // This doesn't include TabId, which Tableau Server would consider external. Consider renaming the function later on.
    return this.isAuthenticationSaml() || this.isAuthenticationOpenId();
  }

  public siteAuthDefaultLabel(): string {
    if (this.isAuthenticationSaml()) {
      return Messages.siteAuthenticationServerLevelAuthSAML();
    } else if (ServerSettingsUnauthenticated.isServerDomainFamilyLocal()) {
      return Messages.localDomain();
    } else if (ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory()) {
      return Messages.activeDirectory();
    } else {
      return Messages.externalDomain();
    }
  }

  public siteAuthDefault(): string {
    if (this.isAuthenticationSaml()) {
      return SiteDefaultAuth[SiteDefaultAuth.SAML];
    } else if (ServerSettingsUnauthenticated.isServerDomainFamilyLocal()) {
      return SiteDefaultAuth[SiteDefaultAuth.Local];
    } else if (ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory()) {
      return SiteDefaultAuth[SiteDefaultAuth.ActiveDirectory];
    } else {
      return SiteDefaultAuth[SiteDefaultAuth.External];
    }
  }

  private getNgHttpService(): IHttpService {
    return ServiceLocator.get<IHttpService>('$http');
  }

  private getQService(): IQService {
    return ServiceLocator.get<ng.IQService>('$q');
  }

}

const authenticationServiceInstance = new AuthenticationService();

export type {AuthenticationService};

export function getAuthenticationServiceInstance(): AuthenticationService {
  return authenticationServiceInstance;
}

export function getAuthenticationServiceNewInstanceForTestPurposeOnly(): AuthenticationService {
  return new AuthenticationService();
}